import { Fragment, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import type { ActivityCell } from "@/lib/analytics/types";
import { cn } from "@/lib/utils";

interface ActivityHeatmapProps {
  cells: ActivityCell[];
  utc: boolean;
  onToggleTz: () => void;
}

// Monday-first: must match the Monday-first convention in bucketByDayHour.
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function cellClasses(count: number, max: number): string {
  if (max === 0 || count === 0) return "bg-muted/40";
  const ratio = count / max;
  if (ratio < 0.2) return "bg-chart-2/25";
  if (ratio < 0.4) return "bg-chart-2/45";
  if (ratio < 0.6) return "bg-chart-2/60";
  if (ratio < 0.8) return "bg-chart-2/80";
  return "bg-chart-2";
}

export function ActivityHeatmap({ cells, utc, onToggleTz }: ActivityHeatmapProps) {
  const [hovered, setHovered] = useState<ActivityCell | null>(null);
  const max = cells.reduce((m, c) => (c.count > m ? c.count : m), 0);

  const grid: ActivityCell[][] = [];
  for (let day = 0; day < 7; day++) {
    const row = cells.filter((c) => c.dayOfWeek === day).sort((a, b) => a.hour - b.hour);
    grid.push(row);
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitle>Activity heatmap</CardTitle>
          <CardDescription>
            Run starts by weekday × hour ({utc ? "UTC" : "local"})
            {hovered ? (
              <span className="ml-2 text-foreground">
                {DAYS[hovered.dayOfWeek]} {String(hovered.hour).padStart(2, "0")}:00
                &mdash; {hovered.count} runs
              </span>
            ) : null}
          </CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={onToggleTz}>
          {utc ? "Show local" : "Show UTC"}
        </Button>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <div className="inline-grid" style={{ gridTemplateColumns: "2.5rem repeat(24, 1fr)" }}>
            <div />
            {Array.from({ length: 24 }, (_, h) => (
              <div
                key={`h-${h}`}
                className="text-[10px] text-muted-foreground text-center tabular-nums"
              >
                {h % 3 === 0 ? h : ""}
              </div>
            ))}
            {grid.map((row, day) => (
              <Fragment key={`row-${day}`}>
                <div className="text-xs text-muted-foreground pr-2 flex items-center">
                  {DAYS[day]}
                </div>
                {row.map((c) => (
                  <div
                    key={`c-${day}-${c.hour}`}
                    className={cn(
                      "aspect-square rounded-[2px] m-[1px] transition-colors cursor-pointer",
                      cellClasses(c.count, max),
                    )}
                    onMouseEnter={() => setHovered(c)}
                    onMouseLeave={() => setHovered(null)}
                    title={`${DAYS[c.dayOfWeek]} ${c.hour}:00 — ${c.count} runs`}
                  />
                ))}
              </Fragment>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
